import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { KeyRound, Radio, Undo2, Play, Square, Pause, RotateCcw, ArrowLeft, Zap, AlertTriangle, Target, Trash2 } from "lucide-react";
import { Section, TeamLogo } from "../components/UIBits";
import { phaseLabel } from "../lib/constants";
import { formatDayLocalized, useT } from "../lib/i18n";
import api from "../lib/api";

const STORAGE_KEY = (num) => `du_marker_token_${num}`;
const STATUS_LABEL = { scheduled: "À venir", live: "En cours", final: "Terminée" };

export default function LiveGamePage() {
    const { num } = useParams();
    const gameNum = parseInt(num, 10);
    const { lang } = useT();
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY(num)) || "");
    const [password, setPassword] = useState("");
    const [loginErr, setLoginErr] = useState("");
    const [state, setState] = useState(null);

    // ------- Auth -------
    const login = async (e) => {
        e?.preventDefault();
        setLoginErr("");
        try {
            const { data } = await api.post(`/games/${gameNum}/marker_login`, { password: password.trim() });
            localStorage.setItem(STORAGE_KEY(num), data.token);
            setToken(data.token);
        } catch (ex) {
            setLoginErr(ex?.response?.data?.detail || "Erreur de connexion");
        }
    };

    const logout = () => { localStorage.removeItem(STORAGE_KEY(num)); setToken(""); setState(null); };

    // ------- State fetch -------
    const load = async () => {
        if (!token) return;
        try {
            const { data } = await api.get(`/games/${gameNum}/marker_state`, { headers: { Authorization: `Bearer ${token}` } });
            setState(data);
        } catch (ex) {
            if (ex?.response?.status === 401 || ex?.response?.status === 403) logout();
        }
    };
    useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, [token, gameNum]);

    if (!token) {
        return (
            <Section>
                <Helmet><title>{`LiveGame #${gameNum} — Marqueur`}</title></Helmet>
                <div className="max-w-md mx-auto" data-testid="marker-login">
                    <Link to="/horaire" className="inline-flex items-center gap-2 text-[#4FE0D5] mb-6 hover:text-[#7FD93C] text-sm"><ArrowLeft size={14} /> Horaire</Link>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Partie #{gameNum}</div>
                    <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-widest mb-3 text-[#7FD93C]">LiveGame</h1>
                    <p className="text-white/70 text-base mb-2">Console de saisie en direct pour les marqueurs.</p>
                    <p className="text-white/50 text-sm mb-8">Demandez le <span className="text-[#7FD93C] font-semibold">code LIVE</span> à un administrateur du tournoi.</p>
                    <form onSubmit={login} className="glass-card p-6 sm:p-8 border-l-4 border-[#7FD93C]">
                        <label className="label-mini flex items-center gap-2 mb-3 text-[#7FD93C]"><KeyRound size={14} /> Code LIVE</label>
                        <input
                            autoFocus
                            className="input-field text-center font-mono text-3xl sm:text-4xl tracking-[0.4em] uppercase py-4"
                            value={password}
                            onChange={e => setPassword(e.target.value.toUpperCase())}
                            data-testid="marker-password-input"
                            placeholder="ABC123"
                            maxLength={12}
                            autoComplete="off"
                            inputMode="text"
                        />
                        {loginErr && <div className="text-rose-400 text-sm mt-3 text-center" data-testid="marker-login-err">{loginErr}</div>}
                        <button type="submit" className="btn-primary w-full mt-5 justify-center text-base py-3" data-testid="marker-login-btn">Accéder à la console</button>
                    </form>
                    <p className="text-center text-white/40 text-xs mt-6">Le même code fonctionne pour toutes les parties du tournoi.</p>
                </div>
            </Section>
        );
    }

    if (!state) return <Section><div className="text-white/50">Chargement...</div></Section>;

    return <LiveGameConsole state={state} token={token} gameNum={gameNum} onRefresh={load} onLogout={logout} lang={lang} />;
}

const PERIOD_LENGTH_SECONDS = 12 * 60; // 12 min per period (per tournament rules)

function LiveGameConsole({ state, token, gameNum, onRefresh, onLogout, lang }) {
    const { game, home_players, away_players, events } = state;
    const [modal, setModal] = useState(null); // { type: 'goal'|'penalty', team: 'home'|'away' }

    const authCfg = { headers: { Authorization: `Bearer ${token}` } };

    const post = async (path, body) => { await api.post(path, body, authCfg); await onRefresh(); };
    const patch = async (path, body) => { await api.patch(path, body, authCfg); await onRefresh(); };
    const del = async (path) => { await api.delete(path, authCfg); await onRefresh(); };

    const setStatus = (s) => patch(`/games/${gameNum}/state`, { status: s });
    const setPeriod = (p) => patch(`/games/${gameNum}/state`, { period: parseInt(p, 10) });
    const setClockOnBackend = (c) => patch(`/games/${gameNum}/state`, { clock: c });
    const addShot = (team) => post(`/games/${gameNum}/events`, { type: "shot", team, period: game.period || 1 });
    const undoLast = async () => {
        const last = [...events].reverse()[0];
        if (!last) return;
        if (!window.confirm(`Annuler le dernier événement (${last.type})?`)) return;
        await del(`/games/${gameNum}/events/${last.id}`);
    };

    const playerMap = useMemo(() => {
        const m = {};
        [...home_players, ...away_players].forEach(p => { m[p.id] = p; });
        return m;
    }, [home_players, away_players]);

    // -------- Match clock hoisted state (source of truth for penalty ticker too) -------
    const [clockSeconds, setClockSeconds] = useState(() => {
        const parsed = parseTimeStr(game.clock);
        return parsed > 0 ? parsed : PERIOD_LENGTH_SECONDS;
    });
    const [clockRunning, setClockRunning] = useState(false);
    const lastCommittedRef = useRef(clockSeconds);

    // Sync local clock with backend value when it changes externally (not while running/editing).
    useEffect(() => {
        if (clockRunning) return;
        const parsed = parseTimeStr(game.clock);
        const next = parsed > 0 ? parsed : PERIOD_LENGTH_SECONDS;
        setClockSeconds(next);
        lastCommittedRef.current = next;
    }, [game.clock, clockRunning]);

    // Countdown ticker — 1s while running. Commits to backend every 10s (throttle) + at 0.
    useEffect(() => {
        if (!clockRunning) return;
        const id = setInterval(() => {
            setClockSeconds((s) => {
                const next = Math.max(0, s - 1);
                if (lastCommittedRef.current - next >= 10 || next === 0) {
                    lastCommittedRef.current = next;
                    setClockOnBackend(formatTime(next));
                }
                if (next === 0) setClockRunning(false);
                return next;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [clockRunning]);

    const clockControls = {
        seconds: clockSeconds,
        running: clockRunning,
        start: () => { if (clockSeconds > 0) setClockRunning(true); },
        pause: () => {
            setClockRunning(false);
            lastCommittedRef.current = clockSeconds;
            setClockOnBackend(formatTime(clockSeconds));
        },
        reset: () => {
            setClockRunning(false);
            setClockSeconds(PERIOD_LENGTH_SECONDS);
            lastCommittedRef.current = PERIOD_LENGTH_SECONDS;
            setClockOnBackend(formatTime(PERIOD_LENGTH_SECONDS));
        },
        setManual: (str) => {
            const parsed = parseTimeStr(str);
            setClockSeconds(parsed);
            lastCommittedRef.current = parsed;
            setClockOnBackend(formatTime(parsed));
        },
    };

    // Total elapsed seconds since the game started (across periods) — used to
    // derive penalty timers below. Consistent whether the clock is running or paused.
    const currentPeriod = game.period || 1;
    const gameElapsedSeconds = (currentPeriod - 1) * PERIOD_LENGTH_SECONDS + (PERIOD_LENGTH_SECONDS - clockSeconds);

    return (
        <div data-testid="marker-console">
            <Helmet><title>{`LiveGame #${gameNum} — ${game.status === "live" ? "🟢 EN DIRECT" : "Marqueur"}`}</title></Helmet>
            <Section>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <Link to="/horaire" className="inline-flex items-center gap-2 text-[#4FE0D5] hover:text-[#7FD93C] text-sm"><ArrowLeft size={14} /> Horaire</Link>
                    <button onClick={onLogout} className="btn-ghost text-xs" data-testid="marker-logout">Déconnexion</button>
                </div>

                {/* Scoreboard */}
                <div className="glass-card p-4 sm:p-6 mb-6" data-testid="marker-scoreboard">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 flex flex-wrap items-center gap-2">
                        <span>Partie #{gameNum} • {game.category} • {formatDayLocalized(game.day, lang)} · {game.time} · {game.ice}</span>
                        {game.phase && game.phase !== "preliminaire" && <span className="px-2 py-0.5 bg-[#4FE0D5]/15 text-[#4FE0D5]">{phaseLabel(game.phase, lang)}</span>}
                    </div>
                    <div className="grid grid-cols-3 items-center gap-4 my-4">
                        <SideCol side="home" game={game} team={{ id: game.home_team_id }} label="Local" score={game.home_score} pim={game.home_pim} shots={game.home_shots} />
                        <div className="text-center">
                            <div className="font-display text-6xl sm:text-7xl leading-none">{game.home_score} <span className="text-white/30">—</span> {game.away_score}</div>
                            <div className={`inline-block mt-3 px-3 py-1 text-xs font-bold uppercase tracking-widest ${game.status === "live" ? "bg-[#7FD93C] text-black animate-pulse" : game.status === "final" ? "bg-white/10 text-white/60" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`} data-testid="game-status-badge">{STATUS_LABEL[game.status] || game.status}</div>
                        </div>
                        <SideCol side="away" game={game} team={{ id: game.away_team_id }} label="Visiteur" score={game.away_score} pim={game.away_pim} shots={game.away_shots} />
                    </div>
                </div>

                {/* Controls: status + period + clock */}
                <div className="glass-card p-4 sm:p-5 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <div className="label-mini mb-2">Statut</div>
                        <div className="flex gap-1 flex-wrap">
                            {["scheduled", "live", "final"].map(s => (
                                <button key={s} onClick={() => setStatus(s)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition ${game.status === s ? "bg-[#7FD93C] text-black" : "bg-white/5 hover:bg-white/10 text-white/70"}`} data-testid={`btn-status-${s}`}>
                                    {s === "scheduled" && "À venir"}
                                    {s === "live" && <><Radio size={10} className="inline mr-1" />En cours</>}
                                    {s === "final" && "Terminée"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="label-mini mb-2">Période</div>
                        <div className="flex gap-1">
                            {[1, 2, 3].map(p => (
                                <button key={p} onClick={() => setPeriod(p)} className={`w-10 h-10 font-display text-lg ${game.period === p ? "bg-[#4FE0D5] text-black" : "bg-white/5 hover:bg-white/10 text-white/80"}`} data-testid={`btn-period-${p}`}>{p}</button>
                            ))}
                            <button onClick={() => setPeriod(4)} className={`px-3 h-10 font-display text-sm ${game.period === 4 ? "bg-[#4FE0D5] text-black" : "bg-white/5 hover:bg-white/10 text-white/80"}`} data-testid="btn-period-ot">OT</button>
                        </div>
                    </div>
                    <div>
                        <div className="label-mini mb-2">Chrono (12 min)</div>
                        <MatchClock controls={clockControls} />
                    </div>
                </div>

                {/* Penalty queues — one active countdown per team, others waiting */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <PenaltyPanel
                        side="home"
                        label="Pénalités local"
                        color="#7FD93C"
                        events={events}
                        playerMap={playerMap}
                        gameElapsed={gameElapsedSeconds}
                        onCancel={(evId) => post(`/games/${gameNum}/events/${evId}/cancel`, {})}
                    />
                    <PenaltyPanel
                        side="away"
                        label="Pénalités visiteur"
                        color="#4FE0D5"
                        events={events}
                        playerMap={playerMap}
                        gameElapsed={gameElapsedSeconds}
                        onCancel={(evId) => post(`/games/${gameNum}/events/${evId}/cancel`, {})}
                    />
                </div>

                {/* Event buttons */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
                    <div className="grid gap-2">
                        <button onClick={() => setModal({ type: "goal", team: "home" })} className="btn-primary text-lg py-4 justify-center bg-[#7FD93C] text-black font-display uppercase tracking-widest" data-testid="btn-goal-home"><Zap size={18} /> But local</button>
                        <button onClick={() => setModal({ type: "penalty", team: "home" })} className="btn-ghost py-3 justify-center border-rose-400/30 text-rose-300" data-testid="btn-penalty-home"><AlertTriangle size={14} /> Pénalité local</button>
                        <button onClick={() => addShot("home")} className="btn-ghost py-2 justify-center text-xs" data-testid="btn-shot-home"><Target size={12} /> Tir local ({game.home_shots || 0})</button>
                    </div>
                    <div className="grid gap-2">
                        <button onClick={() => setModal({ type: "goal", team: "away" })} className="btn-primary text-lg py-4 justify-center bg-[#4FE0D5] text-black font-display uppercase tracking-widest" data-testid="btn-goal-away"><Zap size={18} /> But visiteur</button>
                        <button onClick={() => setModal({ type: "penalty", team: "away" })} className="btn-ghost py-3 justify-center border-rose-400/30 text-rose-300" data-testid="btn-penalty-away"><AlertTriangle size={14} /> Pénalité visiteur</button>
                        <button onClick={() => addShot("away")} className="btn-ghost py-2 justify-center text-xs" data-testid="btn-shot-away"><Target size={12} /> Tir visiteur ({game.away_shots || 0})</button>
                    </div>
                </div>

                <button onClick={undoLast} disabled={events.length === 0} className="btn-ghost mb-6 w-full justify-center disabled:opacity-40" data-testid="btn-undo-last"><Undo2 size={14} /> Annuler le dernier événement</button>

                {/* Timeline */}
                <div className="mb-8">
                    <h2 className="label-mini mb-3">Timeline ({events.length})</h2>
                    <div className="space-y-1" data-testid="events-timeline">
                        {[...events].reverse().map(ev => (
                            <EventRow key={ev.id} ev={ev} playerMap={playerMap} homeTeamId={game.home_team_id} />
                        ))}
                        {events.length === 0 && <div className="text-white/40 text-sm italic">Aucun événement pour le moment.</div>}
                    </div>
                </div>
            </Section>

            {modal && (
                <EventModal
                    kind={modal.type}
                    team={modal.team}
                    period={game.period || 1}
                    clock={formatTime(clockSeconds)}
                    homePlayers={home_players}
                    awayPlayers={away_players}
                    onClose={() => setModal(null)}
                    onSubmit={async (payload) => {
                        await post(`/games/${gameNum}/events`, payload);
                        setModal(null);
                    }}
                />
            )}
        </div>
    );
}

function SideCol({ label, score, pim, shots, team, side }) {
    // Purpose: display team logo + label + shots/pim. score comes from parent header.
    return (
        <div className={`flex flex-col items-center gap-2 ${side === "away" ? "sm:items-end" : "sm:items-start"}`}>
            <TeamLogo team={team} size={56} />
            <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
            <div className="text-xs text-white/60 space-x-2">
                <span>TIRS <span className="text-white font-bold">{shots || 0}</span></span>
                <span>PIM <span className="text-white font-bold">{pim || 0}</span></span>
            </div>
        </div>
    );
}

function EventRow({ ev, playerMap, homeTeamId }) {
    const teamLabel = ev.team === "home" ? "LOC" : "VIS";
    const p = ev.player_id ? playerMap[ev.player_id] : null;
    const a1 = ev.assist1_id ? playerMap[ev.assist1_id] : null;
    const a2 = ev.assist2_id ? playerMap[ev.assist2_id] : null;
    const icon = ev.type === "goal" ? "⚡" : ev.type === "penalty" ? "⚠" : "🎯";
    const cancelled = !!ev.cancelled;
    return (
        <div className={`glass-card p-2 flex items-center gap-2 text-sm ${cancelled ? "opacity-50" : ""}`} data-testid={`event-${ev.id}`}>
            <span className="text-lg w-6 text-center shrink-0">{icon}</span>
            <span className="px-2 py-0.5 text-[10px] font-bold bg-white/5 shrink-0">P{ev.period}</span>
            {ev.time && <span className="text-[10px] font-mono text-white/60 shrink-0">{ev.time}</span>}
            <span className={`px-2 py-0.5 text-[10px] font-bold shrink-0 ${ev.team === "home" ? "bg-[#7FD93C]/20 text-[#7FD93C]" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>{teamLabel}</span>
            <span className={`flex-1 min-w-0 truncate text-white/80 ${cancelled ? "line-through" : ""}`}>
                {ev.type === "goal" && (p ? `${p.name} #${p.number}` : "But")}
                {ev.type === "penalty" && (p ? `${p.name} #${p.number} — ${ev.minutes || 0} min` : `Pénalité — ${ev.minutes || 0} min`)}
                {ev.type === "shot" && "Tir au but"}
                {(a1 || a2) && <span className="text-white/50 text-xs ml-1">(A: {a1?.name}{a2 ? `, ${a2.name}` : ""})</span>}
                {ev.reason && <span className="text-white/40 text-xs ml-1"> · {ev.reason}</span>}
            </span>
            {cancelled && <span className="px-2 py-0.5 text-[10px] font-bold bg-rose-500/20 text-rose-300 shrink-0 uppercase tracking-widest">Annulée</span>}
        </div>
    );
}

function EventModal({ kind, team, period, clock, homePlayers, awayPlayers, onClose, onSubmit }) {
    const roster = team === "home" ? homePlayers : awayPlayers;
    const [pid, setPid] = useState("");
    const [a1, setA1] = useState("");
    const [a2, setA2] = useState("");
    const [time, setTime] = useState(clock);
    const [minutes, setMinutes] = useState(1);
    const [reason, setReason] = useState("");

    const submit = (e) => {
        e.preventDefault();
        const payload = {
            type: kind,
            team,
            period,
            time,
            player_id: pid || null,
        };
        if (kind === "goal") {
            payload.assist1_id = a1 || null;
            payload.assist2_id = a2 || null;
        } else if (kind === "penalty") {
            payload.minutes = parseInt(minutes, 10) || 1;
            payload.reason = reason;
        }
        onSubmit(payload);
    };

    return (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto" data-testid="event-modal">
            <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <h2 className="font-display text-xl uppercase tracking-widest">
                        {kind === "goal" ? "But" : "Pénalité"} — {team === "home" ? "Local" : "Visiteur"}
                    </h2>
                    <button onClick={onClose} className="text-white/60 hover:text-white">✕</button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-3">
                    <div>
                        <div className="label-mini mb-1">Joueur</div>
                        <select className="input-field" value={pid} onChange={e => setPid(e.target.value)} required data-testid="modal-player">
                            <option value="">— Choisir —</option>
                            {roster.sort((a, b) => (parseInt(a.number, 10) || 0) - (parseInt(b.number, 10) || 0)).map(p => (
                                <option key={p.id} value={p.id}>#{p.number} {p.name}</option>
                            ))}
                        </select>
                    </div>
                    {kind === "goal" && (
                        <>
                            <div>
                                <div className="label-mini mb-1">Aide 1 (opt.)</div>
                                <select className="input-field" value={a1} onChange={e => setA1(e.target.value)} data-testid="modal-assist1">
                                    <option value="">— Aucune —</option>
                                    {roster.filter(p => p.id !== pid).map(p => <option key={p.id} value={p.id}>#{p.number} {p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <div className="label-mini mb-1">Aide 2 (opt.)</div>
                                <select className="input-field" value={a2} onChange={e => setA2(e.target.value)} data-testid="modal-assist2">
                                    <option value="">— Aucune —</option>
                                    {roster.filter(p => p.id !== pid && p.id !== a1).map(p => <option key={p.id} value={p.id}>#{p.number} {p.name}</option>)}
                                </select>
                            </div>
                        </>
                    )}
                    {kind === "penalty" && (
                        <>
                            <div>
                                <div className="label-mini mb-1">Minutes</div>
                                <div className="flex gap-1">
                                    {[1, 3, 10].map(m => (
                                        <button type="button" key={m} onClick={() => setMinutes(m)} className={`px-3 py-2 font-display flex-1 ${minutes === m ? "bg-[#7FD93C] text-black" : "bg-white/5 hover:bg-white/10"}`} data-testid={`modal-pen-${m}`}>{m}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="label-mini mb-1">Motif (opt.)</div>
                                <input className="input-field" value={reason} onChange={e => setReason(e.target.value)} placeholder="Ex : Bâton élevé" data-testid="modal-reason" />
                            </div>
                        </>
                    )}
                    <div>
                        <div className="label-mini mb-1">Temps (mm:ss)</div>
                        <input className="input-field font-mono text-center" value={time} onChange={e => setTime(e.target.value)} placeholder="12:34" data-testid="modal-time" />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                        <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
                        <button type="submit" className="btn-primary" data-testid="modal-submit">Confirmer</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*   MatchClock — stopwatch (count up mm:ss) with Start / Pause / Reset       */
/*   Client-side tick every second; commits to backend on pause and every 10s */
/*   Also editable manually via the input field (fallback).                    */
/* -------------------------------------------------------------------------- */
function parseTimeStr(str) {
    if (!str) return 0;
    const m = String(str).trim().match(/^(\d+):(\d{1,2})$/);
    if (!m) return 0;
    return (parseInt(m[1], 10) || 0) * 60 + (parseInt(m[2], 10) || 0);
}

function formatTime(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function MatchClock({ controls }) {
    const { seconds, running, start, pause, reset, setManual } = controls;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(formatTime(seconds));

    const commitEdit = () => {
        setEditing(false);
        setManual(draft);
    };

    const isFinished = seconds === 0 && !running;

    return (
        <div className="flex flex-col gap-2" data-testid="match-clock">
            {editing ? (
                <input
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    className="input-field font-mono text-3xl text-center tracking-widest"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditing(false); }}
                    placeholder="12:00"
                    data-testid="clock-input"
                />
            ) : (
                <button
                    type="button"
                    onDoubleClick={() => { setDraft(formatTime(seconds)); setEditing(true); }}
                    className={`font-mono text-3xl sm:text-4xl text-center tracking-widest py-2 border transition ${
                        running
                            ? "border-[#7FD93C] text-[#7FD93C] bg-[#7FD93C]/5"
                            : isFinished
                                ? "border-rose-400 text-rose-300 bg-rose-500/5"
                                : "border-white/10 bg-black/30 text-white"
                    }`}
                    title="Double-clic pour saisir manuellement"
                    data-testid="clock-display"
                >
                    {formatTime(seconds)}
                </button>
            )}
            <div className="flex gap-1">
                {running ? (
                    <button
                        type="button"
                        onClick={pause}
                        className="flex-1 py-2 bg-white/10 hover:bg-white/15 text-white flex items-center justify-center gap-1 font-bold uppercase text-xs tracking-widest"
                        data-testid="clock-pause"
                    >
                        <Pause size={14} /> Pause
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={start}
                        disabled={seconds <= 0}
                        className="flex-1 py-2 bg-[#7FD93C] hover:bg-[#8fe94c] text-black flex items-center justify-center gap-1 font-bold uppercase text-xs tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="clock-start"
                    >
                        <Play size={14} /> Start
                    </button>
                )}
                <button
                    type="button"
                    onClick={reset}
                    className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center gap-1 text-xs"
                    title="Remettre à 12:00"
                    data-testid="clock-reset"
                >
                    <RotateCcw size={14} /> <span className="font-mono">12:00</span>
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*   PenaltyPanel — per team.                                                 */
/*   Derives active/queued penalties from the event log, using the match      */
/*   clock ("gameElapsed" seconds since game start) as the source of truth.   */
/*   Only ONE penalty can tick down at a time per team. Newer penalties are   */
/*   queued and start once the current one expires.                            */
/* -------------------------------------------------------------------------- */
function PenaltyPanel({ side, label, color, events, playerMap, gameElapsed, onCancel }) {
    // Filter penalty events for this side, EXCLUDING cancelled ones (they stay in the
    // timeline as historical record but don't count toward the active queue).
    const penalties = React.useMemo(() => {
        const sideEvents = events
            .filter((e) => e.type === "penalty" && e.team === side && !e.cancelled && (e.minutes || 0) > 0)
            .map((e) => {
                const secondsIntoPeriod = PERIOD_LENGTH_SECONDS - parseTimeStr(e.time || "");
                const loggedAt = Math.max(0, (e.period - 1) * PERIOD_LENGTH_SECONDS + secondsIntoPeriod);
                return { ev: e, loggedAt, duration: (e.minutes || 0) * 60 };
            })
            .sort((a, b) => a.loggedAt - b.loggedAt);

        // Build queue: start_at = max(logged_at, previous_end_at)
        let previousEnd = 0;
        return sideEvents.map((p) => {
            const startAt = Math.max(p.loggedAt, previousEnd);
            const endAt = startAt + p.duration;
            previousEnd = endAt;
            return { ...p, startAt, endAt };
        });
    }, [events, side]);

    // Split into finished / active / queued at current gameElapsed.
    const rows = penalties.map((p) => {
        if (p.endAt <= gameElapsed) return { ...p, status: "done" };
        if (p.startAt > gameElapsed) {
            return { ...p, status: "queued", remaining: p.duration, waitingFor: p.startAt - gameElapsed };
        }
        return { ...p, status: "active", remaining: p.endAt - gameElapsed };
    });

    const activeRows = rows.filter((r) => r.status !== "done");

    return (
        <div className="glass-card p-3 sm:p-4" data-testid={`penalty-panel-${side}`}>
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                <div className="label-mini" style={{ color }}>{label}</div>
                <div className="ml-auto text-[10px] text-white/40 uppercase tracking-widest">
                    {activeRows.length === 0 ? "aucune" : `${activeRows.length} en cours`}
                </div>
            </div>
            {activeRows.length === 0 ? (
                <div className="text-white/40 text-xs italic py-2">Aucune pénalité active.</div>
            ) : (
                <div className="space-y-2">
                    {activeRows.map((r) => {
                        const p = playerMap[r.ev.player_id];
                        const isActive = r.status === "active";
                        const cancel = async () => {
                            if (!onCancel) return;
                            const who = p ? `#${p.number} ${p.name}` : "cette pénalité";
                            const msg = isActive
                                ? `Annuler la pénalité de ${who} (but marqué contre l'équipe punie) ?\nElle restera dans le sommaire avec la mention « annulée ».`
                                : `Annuler la pénalité en attente de ${who} ?\nElle restera dans le sommaire avec la mention « annulée ».`;
                            if (!window.confirm(msg)) return;
                            await onCancel(r.ev.id);
                        };
                        return (
                            <div
                                key={r.ev.id}
                                className={`flex items-center gap-2 p-2 border-l-4 ${isActive ? "border-rose-400 bg-rose-500/5" : "border-white/20 bg-white/5"}`}
                                data-testid={`pen-${side}-${r.ev.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm truncate">
                                        {p ? `#${p.number} ${p.name}` : "Joueur inconnu"}
                                    </div>
                                    <div className="text-[11px] text-white/50">
                                        {r.ev.minutes} min{r.ev.reason ? ` · ${r.ev.reason}` : ""}
                                        {r.status === "queued" && <span className="ml-1 text-[#4FE0D5]">· en attente</span>}
                                    </div>
                                </div>
                                <div className={`font-mono text-2xl tracking-widest tabular-nums shrink-0 ${isActive ? "text-rose-300" : "text-white/40"}`} data-testid={`pen-time-${r.ev.id}`}>
                                    {formatTime(r.remaining)}
                                </div>
                                <button
                                    type="button"
                                    onClick={cancel}
                                    className="p-2 text-white/40 hover:text-rose-300 hover:bg-rose-500/10 shrink-0"
                                    title={isActive ? "Annuler (but marqué contre l'équipe punie)" : "Annuler la pénalité"}
                                    data-testid={`btn-cancel-pen-${r.ev.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

